import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import "./AuthPage.css";
import { verifyEmailWithCode } from "../services/auth";

const PUBLIC_URL = process.env.PUBLIC_URL || "";

type VerifyStep = "checking" | "success" | "invalid" | "expired";

const VerifyEmailPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const oobCode = searchParams.get("oobCode") || searchParams.get("oob_code") || "";

  const [step, setStep] = useState<VerifyStep>(oobCode ? "checking" : "invalid");
  const [error, setError] = useState("");

  useEffect(() => {
    if (!oobCode) return;
    let cancelled = false;
    (async () => {
      try {
        await verifyEmailWithCode(oobCode);
        if (!cancelled) setStep("success");
      } catch (err: unknown) {
        if (cancelled) return;
        const code = err && typeof err === "object" && "code" in err ? (err as { code: string }).code : "";
        if (code === "auth/expired-action-code") {
          setStep("expired");
        } else {
          setStep("invalid");
        }
        const msg = err instanceof Error ? err.message : "";
        setError(msg);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [oobCode]);

  const logoUrl = PUBLIC_URL ? `${PUBLIC_URL}/logo.svg` : "/logo.svg";

  if (step === "checking") {
    return (
      <div className="auth-page">
        <div className="auth-card auth-card--forgot auth-card--loading">
          <img src={logoUrl} alt="Logo" className="auth-logo" />
          <div className="auth-loading-spinner" aria-label="Verifying link" />
        </div>
      </div>
    );
  }

  if (step === "success") {
    return (
      <div className="auth-page">
        <div className="auth-card auth-card--forgot">
          <img src={logoUrl} alt="Logo" className="auth-logo" />
          <h1 className="auth-title">Account verified</h1>
          <p className="auth-subtitle auth-subtitle--forgot">
            Your account has been successfully verified. You can now access your account.
          </p>
          <button
            type="button"
            className="auth-btn"
            onClick={() => navigate("/login")}
          >
            Login
          </button>
        </div>
      </div>
    );
  }

  if (step === "invalid") {
    return (
      <div className="auth-page">
        <div className="auth-card auth-card--forgot">
          <img src={logoUrl} alt="Logo" className="auth-logo" />
          <h1 className="auth-title">Invalid verification link</h1>
          <p className="auth-subtitle auth-subtitle--forgot">
            We&apos;re sorry, but it looks like this link has already been used or is invalid.
          </p>
          {error && (
            <p className="auth-field-error" role="alert">
              {error}
            </p>
          )}
          <button
            type="button"
            className="auth-btn"
            onClick={() => navigate("/login")}
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  // expired
  return (
    <div className="auth-page">
      <div className="auth-card auth-card--forgot">
        <img src={logoUrl} alt="Logo" className="auth-logo" />
        <h1 className="auth-title">Expired verification link</h1>
        <p className="auth-subtitle auth-subtitle--forgot">
          We&apos;re sorry, but it looks like this link you were trying to use has expired.
        </p>
        <p className="auth-subtitle auth-subtitle--forgot--text">
          You can request a new verification link from the sign-up page.
        </p>
        <button
          type="button"
          className="auth-btn"
          onClick={() => navigate("/signup")}
        >
          Resend link
        </button>
        <p className="auth-bottom auth-bottom--forgot">
          Back to <Link to="/login" className="auth-link">Log in</Link>
        </p>
      </div>
    </div>
  );
};

export default VerifyEmailPage;

